import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Link,
    Alert,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<{ message: string; reason: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const getErrorMessage = (error: any): { message: string; reason: string } => {
        // Log the full error object to see its structure
        console.log('Firebase error in getErrorMessage:', {
            error,
            code: error?.code,
            message: error?.message,
            name: error?.name
        });
        
        // Handle Firebase Auth errors
        const errorCode = error?.code || '';
        const errorMessage = error?.message || '';
        
        // Log the error code we're checking
        console.log('Checking error code:', errorCode);
        
        switch (errorCode) {
            case 'auth/user-not-found':
                return {
                    message: 'Account not found',
                    reason: 'No account exists with this email address'
                };
            case 'auth/wrong-password':
                return {
                    message: 'Invalid password',
                    reason: 'The password you entered is incorrect'
                };
            case 'auth/invalid-email':
                return {
                    message: 'Invalid email',
                    reason: 'Please enter a valid email address'
                };
            case 'auth/too-many-requests':
                return {
                    message: 'Too many attempts',
                    reason: 'Please try again later'
                };
            case 'auth/network-request-failed':
                return {
                    message: 'Network error',
                    reason: 'Please check your internet connection'
                };
            case 'auth/invalid-credential':
                return {
                    message: 'Invalid credentials',
                    reason: 'The email or password you entered is incorrect'
                };
            default:
                // Log unhandled error codes for debugging
                console.log('Unhandled Firebase error:', {
                    code: errorCode,
                    message: errorMessage
                });
                return {
                    message: 'Login failed',
                    reason: 'Please check your email and password'
                };
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await AuthService.signIn(email, password);
            navigate('/'); // Redirect to home page after successful login
        } catch (err: any) {
            // Add detailed error logging
            console.log('Login error details:', {
                error: err,
                code: err.code,
                message: err.message,
                name: err.name,
                stack: err.stack
            });
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    gutterBottom
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(145deg, #1e40af 0%, #2563eb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Log in
                </Typography>

                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            width: '100%', 
                            mb: 2,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ fontWeight: 'bold' }}>{error.message}</Box>
                        <Box sx={{ mt: 0.5, color: 'error.dark' }}>{error.reason}</Box>
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Link 
                            href="/forgot-password"
                            variant="body2"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/forgot-password');
                            }}
                        >
                            Forgot Password?
                        </Link>
                        <Link 
                            href="/signup" 
                            variant="body2"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/signup');
                            }}
                        >
                            {"Don't have an account? Sign Up"}
                        </Link>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default LoginPage; 